import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { dramas } from "@/mocks/dramas";
import { getDramaById } from "@/utils/media";

export default function DramaDetail() {
  const { id } = useParams();
  const drama = getDramaById(Number(id));
  const { toggleFavorite, isFavorite } = useFavorites();
  const [shared, setShared] = useState(false);
  const [expanded, setExpanded] = useState(false);

  usePageMeta({
    title: drama ? `${drama.title}剧情介绍 - 韩剧TV` : "剧集未找到 - 韩剧TV",
    description: drama
      ? `${drama.title}（${drama.year}）${drama.genres.join("、")}题材韩剧，由${drama.director}执导，${drama.cast.join("、")}主演。${drama.synopsis[0]}`
      : "韩剧TV 剧集详情页",
    keywords: drama ? `${drama.title},${drama.genres.join(",")},韩剧剧情,韩剧介绍` : "韩剧剧集",
  });

  if (!drama) {
    return (
      <div className="flex w-full flex-col items-center justify-center px-4 py-24 text-center md:py-32">
        <i className="ri-film-line text-6xl text-foreground-300"></i>
        <h1 className="mt-6 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">未找到该剧集</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          这部剧的信息暂时不可用，可能是链接有误。去剧集大全逛逛吧。
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/category/drama" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 hover:bg-primary-600 cursor-pointer">
            浏览剧集大全
          </Link>
          <Link to="/" className="whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer">
            回到首页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(drama.id);
  const related = dramas
    .filter((d) => d.id !== drama.id && (d.genres.some((g) => drama.genres.includes(g)) || d.director === drama.director))
    .slice(0, 4);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // ignore
    }
    setShared(true);
    window.setTimeout(() => setShared(false), 2000);
  };

  const infoRows = [
    { label: "导演", value: drama.director },
    { label: "编剧", value: drama.writer },
    { label: "主演", value: drama.cast.join("、") },
    { label: "首播", value: `${drama.year} 年` },
    { label: "集数", value: `${drama.episodes} 集` },
    { label: "地区", value: drama.region },
  ];

  return (
    <div className="w-full">
      {/* 头图 */}
      <div className="relative h-[320px] w-full overflow-hidden md:h-[420px]">
        <img src={drama.banner} alt={`${drama.title} 剧照`} title={`${drama.title} 韩剧剧情介绍`} className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/20"></div>
      </div>

      <div className="w-full px-4 md:px-8">
        <div className="-mt-28 md:-mt-32">
          <Breadcrumb light items={[{ label: "首页", to: "/" }, { label: "剧集大全", to: "/category/drama" }, { label: drama.title }]} />

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end">
            <div className="h-56 w-40 shrink-0 overflow-hidden rounded-xl border-4 border-background-50 bg-background-200 md:h-72 md:w-52">
              <img src={drama.cover} alt={`${drama.title} 海报`} title={`${drama.title} 海报`} className="h-full w-full object-cover object-top" />
            </div>
            <div className="min-w-0 flex-1 pt-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50">{drama.status}</span>
                {drama.genres.map((g) => (
                  <span key={g} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700">{g}</span>
                ))}
              </div>
              <h1 className="mt-3 font-heading text-3xl font-800 text-foreground-950 md:text-4xl">{drama.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1 font-600 text-accent-600">
                  <i className="ri-star-fill"></i> {drama.rating.toFixed(1)} 分
                </span>
                <span>{drama.year}</span>
                <span>{drama.episodes} 集</span>
                <span className="flex items-center gap-1"><i className="ri-fire-line text-primary-500"></i> 热度 {drama.hot}</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(drama.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-500 transition-colors cursor-pointer ${
                    favorited
                      ? "bg-accent-500 text-accent-950 hover:bg-accent-600"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {favorited ? "已收藏" : "收藏剧集"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-500 text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
              {shared && (
                <p className="mt-3 flex items-center gap-2 text-sm text-primary-600">
                  <i className="ri-checkbox-circle-line"></i> 链接已复制到剪贴板。
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0">
            {/* 基本信息 */}
            <section>
              <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">基本信息</h2>
              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 rounded-xl border border-background-200/70 bg-background-50 p-5 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-14 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {/* 剧情简介 */}
            <section className="mt-10">
              <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">剧情简介</h2>
              <div className="mt-5 space-y-4">
                {drama.synopsis.map((p, i) => (
                  <p key={i} className="text-base leading-8 text-foreground-800">{p}</p>
                ))}
              </div>
            </section>

            {/* 剧照 */}
            <section className="mt-10">
              <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">剧照欣赏</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {drama.gallery.map((src, i) => (
                  <div key={i} className="aspect-[4/3] overflow-hidden rounded-lg bg-background-200">
                    <img src={src} alt={`${drama.title} 剧照 ${i + 1}`} title={`${drama.title} 剧照`} className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-105" />
                  </div>
                ))}
              </div>
            </section>

            {/* 分集大纲 */}
            <section className="mt-10">
              <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">分集大纲</h2>
              <p className="mt-2 text-sm text-foreground-500">演示数据，仅展示部分剧集信息。</p>
              <ul className="mt-5 divide-y divide-background-200/70 rounded-xl border border-background-200/70 bg-background-50">
                {(expanded ? drama.episodeList : drama.episodeList.slice(0, 3)).map((ep) => (
                  <li key={ep.no} className="flex gap-4 p-4 md:p-5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 font-label text-sm font-600 text-primary-700">
                      {ep.no}
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-600 text-foreground-900">第{ep.no}集 · {ep.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{ep.summary}</p>
                    </div>
                  </li>
                ))}
              </ul>
              {drama.episodeList.length > 3 && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-4 flex items-center gap-1 text-sm font-500 text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  {expanded ? "收起分集" : `展开全部 ${drama.episodeList.length} 集`}
                  <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                </button>
              )}
            </section>
          </div>

          {/* 侧栏推荐 */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-xl border border-background-200/70 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-700 text-foreground-950">
                <i className="ri-magic-line text-primary-500"></i> 相关推荐
              </h3>
              <p className="mt-1 text-xs text-foreground-500">同类型 / 同导演作品</p>
              <div className="mt-4 space-y-4">
                {related.map((d) => (
                  <DramaCard key={d.id} drama={d} variant="list" />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="w-full px-4 py-12 md:px-8 md:py-16">
        <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">你可能还喜欢</h2>
        <div className="mt-6 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
          {dramas.filter((d) => d.id !== drama.id).slice(0, 4).map((d) => (
            <DramaCard key={d.id} drama={d} />
          ))}
        </div>
      </section>
    </div>
  );
}